import {
  Appointment,
  AppointmentProduct,
  Clinic,
  Lab,
  TimeSlot,
  AppointmentType,
  ReduxState,
  PartnerClinicDetails,
  theme
} from 'kb-shared';
import get from 'lodash.get';
import React, { Component, useEffect } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import AuthFlowLayout, { PageTitle } from '../../components/AuthFlowLayout';
import { WhiteButton } from '../../components/Buttons';
import MobileStepTracker from '../../components/MobileStepTracker';
import BubbleStepTracker from '../../components/BubbleStepTracker';
import AppointmentTimeCard from './components/BookingCalendarTime/AppointmentTimeCard';
import BookingCalendarTime from './components/BookingCalendarTime/BookingCalendarTime';
import SkipToSignUpCTA from './components/SkipToSignUpCTA';
import ConfirmOrder from './steps/ConfirmOrder';
import CreateEnterpriseMembership from './steps/CreateEnterpriseMembership';
import CreateAccount, { CreateSubStep } from './steps/CreateAccount';
import PurchaseConfirmation from './steps/PurchaseConfirmation';
import SelectLocationVirtual from './steps/SelectLocationVirtual';
import SelectOption from './steps/SelectOption';
import BackButton from '../../components/Button/BackButton';
import { leftArrow2 } from '../../../src/assets/icons';
import { PartnerLocationSearch, PartnerLocationRequest } from './steps/PartnerLocation';
import BookingStateManager, { Data, Membership, StepType } from './utils/BookingStateManager';
import { gql } from 'apollo-boost';
import { useQuery } from 'react-apollo';
import EmployerBenefitCTA from 'screens/BookingQuiz/components/EmployerBenefitCTA';
import { WhenAppointmentsAvailable } from './components/BookingCalendarTime/WhenAppointmentsAvailable';

// Steps
// Utils
// Types
const Container = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: content-box;
`;

const MobileStepTrackerWrapper = styled.div`
  padding-top: 24px;
  padding-bottom: 24px;
`;

const BubbleStepTrackerWrapper = styled.div`
  padding-top: 24px;
  display: flex;
  justify-content: center;
`;

const SignUpCTAWrapper = styled.div`
  padding-top: 28px;
`;

const DashboardButton = styled(WhiteButton)`
  margin: 36px auto;
`;

type Props = RouteComponentProps & {
  isLoggedIn: boolean;
  skipToSignUp?: boolean;
};

type State = {
  data: Data;
  createAccountSubstep: CreateSubStep;
  partnerClinicSelected: PartnerClinicDetails | null;
};

class Book extends Component<Props, State> {
  dataManager: BookingStateManager;

  constructor(props: Props) {
    super(props);

    this.dataManager = new BookingStateManager(props.history, this.updateData, props.isLoggedIn);
    this.state = {
      data: this.dataManager.data,
      createAccountSubstep: 'choose_signup_option',
      partnerClinicSelected: null
    };

    if (props.skipToSignUp) {
      this.dataManager.skipToSignUp();
    }
  }

  componentDidMount() {
    this.setState({
      data: this.dataManager.data
    });
  }

  hasEmployer = () => get(this.state.data, 'selectedProduct.data.employer', false);

  updateData = (data: Data) => {
    this.setState({
      data: data
    });
  };

  getAppointment(product: AppointmentProduct) {
    if (product.type === 'appointment') {
      return product.data;
    } else if (product.type === 'reschedule_appointment') {
      return product.data.appointmentType;
    }
  }

  getPageTitle(stepType: StepType) {
    const { selectedProduct, partnerClinicSearch } = this.state.data;
    const partnerClinicSelected = this.state.partnerClinicSelected;
    // @ts-ignore
    const appointment = (selectedProduct && this.getAppointment(selectedProduct)) || null;

    switch (stepType) {
      case 'select_appointment':
        return 'Select an option below:';
      case 'location':
        if (partnerClinicSearch == 'search' && !partnerClinicSelected) {
          return 'Find a Partner Clinic';
        } else if (partnerClinicSelected) {
          return '';
        } else if (appointment && appointment.virtual) {
          return 'Choose a time for your virtual consult';
        } else {
          return 'Great! Where would you like to meet us?';
        }
      case 'create_account':
        switch (this.state.createAccountSubstep) {
          case 'choose_signup_option':
            return 'Create an account:';
          case 'email':
            return 'Sign up with email:';
          case 'google_attributes':
            return 'Tell us about yourself:';
          case 'login':
            return 'Welcome back!';
          case 'forgot_password':
            return 'Create your new password:';
          case 'verification_code':
            return 'Enter verification code:';
          default:
            return 'Create an account:';
        }
      case 'time':
        if (appointment && appointment.virtual) {
          return 'Choose a time for your virtual consult';
        } else {
          return '';
        }
      case 'confirm':
        if (selectedProduct && selectedProduct.type === 'enterprise_membership') {
          return 'Activate Membership';
        } else {
          return this.hasEmployer() ? 'Activate Kindbody Membership' : 'Confirmation:';
        }
      case 'success':
        if (partnerClinicSelected) {
          return "We're working to schedule your appointment!";
        } else {
          return "You're all set!";
        }
      default:
        return '';
    }
  }

  getStepsNames(): { stepNames: string[]; currentStep: number; pageTitle: string } {
    const {
      data: { steps, selectedStep }
    } = this.state;
    let currentStep = 0;
    let pageTitle = '';
    const stepNames = Object.keys(steps)
      // @ts-ignore
      .map((key: StepType, index: number) => {
        const step = steps[key];
        if (step && step.type === selectedStep) {
          currentStep = index;
          pageTitle = this.getPageTitle(step.type);
        }
        return (step && step.name) || '';
      })
      .filter((name?: string) => name); // skip blank/hidden steps
    return {
      stepNames,
      currentStep,
      pageTitle
    };
  }

  renderCurrentStep(mobile: boolean) {
    const {
      selectedClinic,
      selectedLab,
      selectedProduct,
      selectedStep,
      selectedTimeSlot,
      purchasedProduct,
      selectedAppointmentTypes,
      partnerClinicSearch
    } = this.state.data;

    const appointment =
      (selectedProduct && selectedProduct.type === 'appointment' && selectedProduct.data) ||
      (selectedProduct &&
        selectedProduct.type === 'reschedule_appointment' &&
        selectedProduct.data.appointmentType);

    switch (selectedStep) {
      case 'select_appointment':
        let selectedAppointment;

        if (selectedProduct && selectedProduct.type === 'appointment') {
          selectedAppointment = selectedProduct.data;
        } else if (selectedProduct && selectedProduct.type === 'reschedule_appointment') {
          selectedAppointment = selectedProduct.data.appointmentType;
        }

        return (
          <SelectOption
            mobileLayout={mobile}
            // @ts-ignore
            selectedAppointment={selectedAppointment}
            onSelect={apt => {
              this.dataManager.setAppointment(apt);
            }}
            onSelectMembership={(membership: Membership) => {
              this.dataManager.addMembershipToCart(membership);
            }}
            onSkipToSignUp={() => {
              this.dataManager.skipToSignUp();
            }}
          />
        );
      case 'location':
        const virtualApptType = selectedAppointmentTypes?.find(apptType => apptType.virtual);
        const inPersonApptType = selectedAppointmentTypes?.find(apptType => !apptType.virtual);
        let directToPartnerClinicSearch = false;

        //@ts-ignore
        if (selectedProduct && selectedProduct.data && selectedProduct.data.category) {
          //@ts-ignore
          directToPartnerClinicSearch = [
            'Fertility',
            'Non-discount code applicable Fertility'
          ].includes(selectedProduct.data.category.name);
          //@ts-ignore
        } else if (inPersonApptType && inPersonApptType.category) {
          //@ts-ignore
          directToPartnerClinicSearch = [
            'Fertility',
            'Non-discount code applicable Fertility'
          ].includes(inPersonApptType.category.name);
          //@ts-ignore
        } else if (virtualApptType && virtualApptType.category) {
          //@ts-ignore
          directToPartnerClinicSearch = [
            'Fertility',
            'Non-discount code applicable Fertility'
          ].includes(virtualApptType.category.name);
        }

        //@ts-ignore
        let appointmentName = selectedProduct?.data?.name;

        if (appointmentName == null) {
          appointmentName = inPersonApptType?.name;
        }
        const partnerClinicSelected = this.state.partnerClinicSelected;
        if (partnerClinicSearch == 'search' && !partnerClinicSelected) {
          return (
            <PartnerLocationSearch
              onSelected={(newPartnerClinicSelected: PartnerClinicDetails) =>
                this.setState({ partnerClinicSelected: newPartnerClinicSelected })
              }
            />
          );
        } else if (partnerClinicSelected) {
          return (
            <PartnerLocationRequest
              partnerClinicSelected={partnerClinicSelected}
              mobileLayout={mobile}
              appointmentName={appointmentName}
              afterSend={() => this.dataManager.setSuccessPartnerClinic()}
            />
          );
        }
        return (
          <SelectLocationVirtual
            appointmentType={appointment || inPersonApptType}
            selectedLab={selectedLab}
            onSelectLab={(lab: Lab | null) => {
              this.dataManager.setLab(lab);
            }}
            selectedClinic={selectedClinic}
            onSelectClinic={(clinic: Clinic | null) => {
              this.dataManager.setClinic(clinic, inPersonApptType);
            }}
            showVirtual={!!virtualApptType}
            onSelectVirtual={() => {
              this.dataManager.setVirtual(virtualApptType);
            }}
            directToPartnerClinicSearch={directToPartnerClinicSearch}
            onContactUsClick={() => this.dataManager.setPartnerClinic()}
          />
        );
      case 'time':
        appointmentName = '';
        if (selectedProduct && selectedProduct.type === 'appointment') {
          selectedAppointment = selectedProduct.data;
          appointmentName = selectedAppointment.name;
        } else if (selectedProduct && selectedProduct.type === 'reschedule_appointment') {
          selectedAppointment = selectedProduct.data.appointmentType;
          appointmentName = selectedAppointment.name;
        }

        return (
          <>
            {(selectedClinic || (appointment && appointment.virtual)) && (
              <AppointmentTimeCard
                appointmentType={appointmentName}
                imageUrl={selectedClinic ? selectedClinic.imageUrl : undefined}
                address={selectedClinic ? selectedClinic.address : 'Virtual, In-home'}
              />
            )}

            <WhenAppointmentsAvailable
              labId={selectedLab ? parseInt(selectedLab.id) : null}
              locationId={selectedClinic ? Number(selectedClinic.id) : null}
              appointmentTypeId={appointment ? parseInt(appointment.id) : null}
            >
              <div style={{ padding: '20px 0px' }}>
                <PageTitle mobile={mobile}>What date & time works best for you?</PageTitle>
              </div>

              {(selectedClinic || (appointment && appointment.virtual)) && appointment && (
                <BookingCalendarTime
                  labId={selectedLab && parseInt(selectedLab.id)}
                  locationId={selectedClinic ? selectedClinic.id : null}
                  appointmentType={appointment}
                  selectedTimeSlot={selectedTimeSlot}
                  onSelectTimeSlot={(slot: TimeSlot | null) => {
                    if (slot != null) {
                      this.dataManager.setTimeSlot(slot);
                    }
                  }}
                />
              )}
            </WhenAppointmentsAvailable>
          </>
        );

      case 'create_account':
        return (
          // @ts-ignore
          <CreateAccount
            // @ts-ignore
            mobileLayout={mobile}
            lab={selectedLab}
            onAuthentication={() => {
              this.dataManager.setUserDidSignUp();
            }}
            onChangeSubstep={(step: CreateSubStep) => {
              this.setState({
                createAccountSubstep: step
              });
            }}
          />
        );
      case 'confirm':
        if (selectedProduct?.type === 'enterprise_membership') {
          return (
            <CreateEnterpriseMembership
              requireUniqueId={this.state.data.requireUniqueId}
              onMembershipCreated={() => {
                this.dataManager.setCreateEnterpriseMembershipSuccess();
              }}
            />
          );
        } else {
          const query = new URLSearchParams(this.props.location.search);
          const isOldMembership = query.get('membership_id');

          if (isOldMembership != null) {
            query.delete('membership_id');
            query.append('enterprise_membership', 'true');

            this.props.history.replace(`${this.props.location.pathname}?${query.toString()}`);
            this.setState({
              data: {
                ...this.state.data,
                selectedProduct: { type: 'enterprise_membership', data: {} }
              }
            });
            return null;
          } else {
            return (
              <>
                <RedirectIfMember product={selectedProduct} history={this.props.history} />
                <ConfirmOrder
                  product={selectedProduct}
                  timeSlot={selectedTimeSlot}
                  clinic={selectedClinic}
                  hasEmployer={this.hasEmployer()}
                  onBookAppointment={(appointment: Appointment) => {
                    this.dataManager.setBookingAppointmentSuccess(appointment);
                  }}
                  onPurchaseMembership={(membership: Membership) => {
                    this.dataManager.setMembershipPurchaseSuccess(membership);
                  }}
                />
              </>
            );
          }
        }
      case 'success':
        appointmentName = '';
        if (selectedProduct && selectedProduct.type === 'appointment') {
          const selectedAppointment = selectedProduct.data;
          appointmentName = selectedAppointment.name;
        }
        if (!appointment && selectedAppointmentTypes) {
          appointmentName = selectedAppointmentTypes[0].name;
        }

        return (
          <>
            <PurchaseConfirmation
              // @ts-ignore
              purchase={purchasedProduct}
              timeSlot={selectedTimeSlot}
              clinic={selectedClinic}
              partnerClinicDetails={this.state.partnerClinicSelected}
              appointmentName={appointmentName}
              history={this.props.history}
              onReturnToBookAppointment={() => {
                // Since we are already on the book state, pushing or replacing '/book' will have no effect since the internal state will not change.
                // Do a hard reload of the page
                this.props.history.push('/booking-quiz');
                window.location.reload();
              }}
            />
            <DashboardButton onClick={() => this.props.history.push('/')}>
              Back to Your Dashboard
            </DashboardButton>
          </>
        );
      default:
        return null;
    }
  }

  renderSkipToSignUpCTA() {
    if (this.props.isLoggedIn || this.state.data.selectedStep === 'create_account') {
      return null;
    } else {
      return (
        <SignUpCTAWrapper>
          <SkipToSignUpCTA
            onClick={() => {
              this.dataManager.skipToSignUp();
            }}
          />
        </SignUpCTAWrapper>
      );
    }
  }

  render() {
    const { stepNames, currentStep, pageTitle } = this.getStepsNames();
    const { isLoggedIn } = this.props;

    let selectedStep = Math.min(currentStep, stepNames.length - 1); // prevent hidden steps from showing as selected
    if (this.state.data.selectedStep === 'time') {
      selectedStep = 1; // keep Location selected on Time step per designs
    }
    const { target } = this.state.data;
    const isEnterpriseMembership = target && target.includes('enterprise_membership=true');

    return (
      <AuthFlowLayout
        title={pageTitle}
        renderAboveTitleContent={(mobile: boolean) => {
          if (this.state.data.selectedStep === 'success') {
            // Don't show the stepper/nav on success
            return null;
          }
          if (mobile)
            return (
              <MobileStepTrackerWrapper>
                <MobileStepTracker steps={stepNames} selectedStep={selectedStep} />
              </MobileStepTrackerWrapper>
            );
          return (
            <>
              {!isLoggedIn && !isEnterpriseMembership && <EmployerBenefitCTA />}
              <BubbleStepTrackerWrapper>
                <BubbleStepTracker
                  steps={stepNames}
                  selectedStep={selectedStep}
                  onSelectStep={(index: number) => {
                    const step = this.dataManager.stepAtIndex(index);
                    this.setState({ partnerClinicSelected: null });
                    this.dataManager.goBacktoStep(step.type);
                  }}
                />
              </BubbleStepTrackerWrapper>
            </>
          );
        }}
        renderMainContent={(mobile: boolean) => {
          // this is a patch to fix title and content overflow inside AuthFlowLayout
          // when the new bubble navigation is above them
          // I rather didnt tried to fix it within the AuthFlowLayout to
          // avoid breaking other pages using AuthFlowLayout
          let inlineCss = {};
          if (!mobile) {
            inlineCss = { paddingTop: '85px' };
          }
          return (
            <Container style={inlineCss}>
              <Content>{this.renderCurrentStep(mobile)}</Content>
              {this.state.data.selectedStep !== 'select_appointment' &&
                this.state.data.selectedStep !== 'success' && (
                  <BackButtonContainer>
                    <BackButton
                      style={{ alignSelf: 'start', marginTop: '85px' }}
                      onClick={() => {
                        this.setState({ partnerClinicSelected: null });
                        history.back();
                      }}
                    >
                      <ArrowIcon src={leftArrow2} />
                      Back
                    </BackButton>
                  </BackButtonContainer>
                )}
            </Container>
          );
        }}
        renderDesktopRightSidebar={() => {
          return null;
        }}
      />
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  isLoggedIn: state.patient.isLoggedIn
});

export default withRouter(connect(mapStateToProps)(Book));

const PATIENT_MEMBERSHIP = gql`
  query patientMembership {
    patient {
      id
      patientMembership {
        id
        membershipRules {
          code
          id
          discountId
        }
      }
    }
  }
`;

// If the user lands on confirm order page to buy a membership, but is already a member, redirect them to book.
const RedirectIfMember = (props: {
  product: AppointmentProduct<AppointmentType> | null;
  history: any;
}) => {
  const { product, history } = props;
  const membership = useQuery(PATIENT_MEMBERSHIP, {
    skip: (product && product.type !== 'membership') || false
  });
  useEffect(() => {
    if (membership.data && membership.data.patient.patientMembership) {
      if (product && product.type === 'membership') {
        history.push('/book');
      }
    }
  }, [membership.data, product, history]);
  return null;
};

const ArrowIcon = styled.img`
  height: 12px;
  width: 40px;
  @media screen and (max-width: ${theme.breakpoints.largePhonePortrait}) {
    margin-right: -14px;
  }
`;

const BackButtonContainer = styled.div`
  @media screen and (min-width: ${theme.breakpoints.tabletPortrait}) {
    width: 90%;
  }
`;
